import {
    Tabs, TabsContent, TabsList, TabsTrigger
} from 'shared/components/ui/tabs';
import '../prompting-and-rag-training.css';
import React, {
    useRef
} from 'react';
import {
    Card, CardContent
} from 'shared/components/ui/card';
import {
    Button
} from 'shared/components/ui/button';
import {
    PageContent, PageDescription, PageHeader, PageHeaderAction, PageLayout, PageSubTitle, PageTitle, PageTitles
} from 'shared/components/NewDesignComponents/PageLayout';
import {
    TextSearch, TextCursorInput, Webhook, ListChecks, Plug
} from 'lucide-react';
import {
    useNavigate
} from 'react-router-dom';

const TabContent = ({
    icon: Icon, title, description, tags, buttonText, buttonAction, disabled
}) => (
    <Card className="w-full rounded h-full shadow-md bg-card overflow-hidden border">
        <CardContent className="p-0 flex flex-col md:flex-row items-center justify-center h-full w-full">
            <div className="h-full min-w-[200px] w-full md:w-1/3">
                <div className="bg-muted w-full h-full flex items-center justify-center p-6 border-r border-border">
                    <Icon className="w-32 h-32 text-[#b71e3d]" />
                </div>
            </div>
            <div className="h-full min-w-[300px] w-full md:w-2/3">
                <div className="px-6 py-4">
                    <div className="font-bold text-xl mb-4 text-center md:text-left">{title}</div>
                    <p className="text-muted-foreground text-sm text-center md:text-left">
                        {description}
                    </p>
                </div>
                <div className="px-6 pt-4 pb-4 text-center md:text-left">
                    {tags.map((tag, index) => (
                        <span key={index} className="inline-block bg-muted/50 rounded-full px-3 py-1 text-xs font-semibold text-foreground mr-2 mb-2">
                            {tag}
                        </span>
                    ))}
                </div>
                <div className="px-6 pb-4 flex justify-center md:justify-start">
                    <Button disabled={disabled} className="w-fit" onClick={buttonAction}>
                        {buttonText}
                    </Button>
                </div>
            </div>
        </CardContent>
    </Card>
);

export default function Laboratory() {

    const navigate = useNavigate();

    const onClickNavigate = (path) => {
        navigate(path);
    };

    const tabs = [
        {
            value: 'prompt',
            label: 'Prompt Lab',
            description: 'Learn how to create a prompt',
            icon: TextCursorInput,
            content: (
                <TabContent
                    disabled={false}
                    icon={TextCursorInput}
                    title="Prompt Lab"
                    description="The Prompt Lab is where intelligent workflows take shape. Design, test, and refine AI instructions as dynamic workflow components. Experiment with different LLMs, multimodal models, and parameter settings to observe how each influences behavior and outcomes ensuring every instruction performs optimally as part of a larger, adaptive enterprise workflow."
                    tags={['#promptengineering', '#contentgeneration', '#workflowdesign']}
                    buttonText="Start Building"
                    buttonAction={() => onClickNavigate('prompt-lab')}
                />
            )
        },
        {
            value: 'api-manager',
            label: 'API Manager',
            description: 'Create and Test APIs',
            icon: Webhook,
            content: (
                <TabContent
                    disabled={false}
                    icon={Webhook}
                    title="API Manager"
                    description="The API Manager equips enterprise workers with the tools to create API definitions and test integrations for use with BeX Opcode builder. Learn to define endpoints, configure request and response structures, and validate API functionality, while exploring advanced techniques such as authentication setup and dynamic parameter handling, all within the API Manager."
                    tags={[]}
                    buttonText="Manage APIs"
                    buttonAction={() => onClickNavigate('api-manager')}
                />
            )
        },
        {
            value: 'mcp-builder',
            label: 'MCP Builder',
            description: 'Configure and Test MCP servers',
            icon: Plug,
            content: (
                <TabContent
                    disabled={false}
                    icon={Plug}
                    title="MCP Builder"
                    description="The MCP Builder lets you register external MCP (Model Context Protocol) servers as tool sources for opcodes. Configure server URL, transport, auth, and headers; test the server live by discovering its tools and invoking one with your own arguments — all before you wire the connection into an opcode tool step."
                    tags={['#mcp', '#tools', '#integration']}
                    buttonText="Manage MCP"
                    buttonAction={() => onClickNavigate('mcp-builder')}
                />
            )
        },
        // {
        //     value: "code",
        //     label: "Code Sandbox",
        //     description: "Use AI to learn programming and try it out in the sandbox",
        //     icon: Code,
        //     content: (
        //         <TabContent
        //             disabled={true}
        //             icon={Code}
        //             title="Code Sandbox"
        //             description="The Code Sandbox is the AI powered code generation and execution environment for building python or JS codes. The code functions can be exported as non LLM instructions for use in OpCodes when deployed."
        //             tags={["#codegeneration", "#sandbox", "#programming"]}
        //             buttonText="Code Sandbox"
        //             buttonAction={() => onClickNavigate(`code-sandbox`)}
        //         />
        //     )
        // },
        // {
        //     value: "teacher",
        //     label: "AI Teacher",
        //     description: "Need help? AI teacher is here to help",
        //     icon: NotebookPen,
        //     content: (
        //         <TabContent
        //             disabled={false}
        //             icon={NotebookPen}
        //             title="AI Teacher"
        //             description="Meet the AI Teacher, our built in learning tool to learn about anything in the BeX Platform, or you can create your customized knowledge based to use in the AI Teacher."
        //             tags={["#learning", "#knowledgebase", "#aiteacher"]}
        //             buttonText="AI Teacher"
        //             buttonAction={() => onClickNavigate(`ai-teacher`)}
        //         />
        //     )
        // },
        // {
        //     value: "tuning_lab",
        //     label: "Tuning Lab",
        //     description: "Tuning Lab Description",
        //     icon: SlidersHorizontal,
        //     content: (
        //         <TabContent
        //             disabled={true}
        //             icon={SlidersHorizontal}
        //             title="Tuning Lab"
        //             description="The Tuning Lab provides the facility to tune your AI Models used in the platform. After tuning, they can be deployed to be used."
        //             tags={["#tuning", "#aimodels", "#deployment"]}
        //             buttonText="Tuning Lab"
        //             buttonAction={() => onClickNavigate(`tuning-lab`)}
        //         />
        //     )
        // },
        {
            value: 'instructions',
            label: 'Instructions',
            description: 'Manage your saved instructions and use them in your workflows',
            icon: TextSearch,
            content: (
                <TabContent
                    disabled={false}
                    icon={TextSearch}
                    title="Instructions"
                    description="An instruction is a carefully crafted system prompt, designed with specific parameters and a chosen foundation model (LLM) to meet your unique needs. It can be saved and seamlessly integrated into any workflow, ensuring consistency and efficiency in task execution."
                    tags={['#systemprompt', '#customai', '#taskautomation']}
                    buttonText="Manage Instructions"
                    buttonAction={() => onClickNavigate('instructions')}
                />
            )
        },
        {
            value: 'ai_instruction_v_and_v',
            label: 'AI Instruction V&V',
            description: 'Validate AI instructions with test vectors and similarity scoring',
            icon: ListChecks,
            content: (
                <TabContent
                    disabled={true}
                    icon={ListChecks}
                    title="Instruction V&V"
                    description="Upload input/output test vectors to verify AI instructions. Run validations, inspect results, and track test performance with embedding-based similarity scoring."
                    tags={['#validation', '#instructions', '#testing', '#embedding']}
                    buttonText="Launch V&V"
                    buttonAction={() => onClickNavigate('instruction-vnv')}
                />
            )
        },
    ];

    const scrollAreaRef = useRef(null);

    return (
        <PageLayout ref={scrollAreaRef}>
            <PageHeader>
                <PageTitles>
                    <PageTitle>
                        Master Prompt Engineering
                    </PageTitle>
                    <PageSubTitle>
                        Unleash the True Potential of AI
                    </PageSubTitle>
                    <PageDescription>
                        Transform the way your business thinks, works, and scales. Design intelligent workflows that merge human intent with machine precision empowering smarter decisions, faster execution, and limitless innovation across your enterprise.
                    </PageDescription>
                </PageTitles>
                <PageHeaderAction>
                    <Button className='w-full' onClick={() => onClickNavigate('prompt-lab')}> Add New Instruction </Button>
                </PageHeaderAction>
            </PageHeader>
            <PageContent>
                <div className="text-center w-full">
                    <span className="text-2xl font-semibold"><span className="text-[#b1092a]">AI Lab</span>: All you need to master prompting</span>
                    <div className="pt-4">
                        <Tabs defaultValue={'prompt' || tabs[0].value} className="flex flex-row w-full">
                            <div className="w-1/2 flex flex-col pr-4">
                                {/* <Textarea
                                    placeholder="Describe what kind of prompt you want to create"
                                    className="mr-4 mb-4 resize-none"
                                /> */}
                                <TabsList className="flex flex-col h-auto w-full space-y-2 p-1 p-2 bg-transparent">
                                    {tabs.map((tab) => (
                                        <TabsTrigger
                                            key={tab?.value}
                                            value={tab?.value}
                                            className="flex items-center justify-start px-4 py-2 w-full data-[state=active]"
                                        >
                                            <tab.icon className="w-8 h-8 min-w-8 min-h-8 mr-4" />
                                            <div className="text-left text-wrap">
                                                <div className="text-sm font-semibold">{tab.label}</div>
                                                <div className="text-xs text-muted-foreground">{tab.description}</div>
                                            </div>
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                            </div>
                            {tabs.map((tab) => (
                                <TabsContent key={tab.value} value={tab.value} className="flex-1 w-1/2">
                                    {tab.content}
                                </TabsContent>
                            ))}
                        </Tabs>
                    </div>
                </div>
            </PageContent>
        </PageLayout>
    );
}